import React, { useEffect } from 'react'

/**
 * Crisp客服聊天组件
 * 负责加载和管理Crisp客服系统
 */
const CrispChat = ({
  websiteId = "7598bf86-9ebb-46bc-8c61-be8929bbf93d",
  enabled = true,
  autoLoad = true
}) => {
  useEffect(() => {
    // 如果未启用，则跳过
    if (!enabled) {
      return
    }

    // 如果已经加载过，则跳过
    if (window.$crisp && window.CRISP_WEBSITE_ID) {
      return
    }

    // 初始化Crisp
    const initializeCrisp = () => {
      try {


        // 设置Crisp全局变量
        window.$crisp = []
        window.CRISP_WEBSITE_ID = websiteId

        // 创建并加载Crisp脚本
        const script = document.createElement('script')
        script.src = 'https://client.crisp.chat/l.js'
        script.async = true
        script.type = 'text/javascript'

        // 添加加载成功和失败的处理
        script.onload = () => {
          // 等待Crisp完全初始化
          setTimeout(() => {
            if (window.$crisp && typeof window.$crisp.push === 'function') {
              try {
                // 设置语言为中文
                window.$crisp.push(['set', 'session:data', {
                  language: 'zh'
                }])

                // 显示原生聊天按钮
                window.$crisp.push(['do', 'chat:show'])
              } catch (error) {
                console.warn('配置Crisp时出错:', error)
              }
            } else {
              // 尝试再次等待
              setTimeout(() => {
                if (window.$crisp && typeof window.$crisp.push === 'function') {
                  window.$crisp.push(['do', 'chat:show'])
                }
              }, 2000)
            }
          }, 2000)
        }

        script.onerror = () => {
          console.error('Crisp脚本加载失败')
        }

        // 将脚本添加到head中
        document.getElementsByTagName('head')[0].appendChild(script)

      } catch (error) {
        console.error('初始化Crisp时出错:', error)
      }
    }

    if (autoLoad) {
      initializeCrisp()
    }

    // 清理函数
    return () => {
      // 注意：Crisp一旦加载就不容易完全移除
      // 这里只是清理我们的引用
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        try {
          // 隐藏聊天窗口
          window.$crisp.push(['do', 'chat:hide'])
        } catch (error) {
          console.warn('隐藏Crisp聊天窗口时出错:', error)
        }
      }
    }
  }, [websiteId, enabled, autoLoad])

  // 这个组件不渲染任何可见内容
  return null
}

/**
 * Crisp客服工具函数
 */
export const CrispUtils = {
  /**
   * 显示聊天窗口
   */
  show: () => {
    if (window.$crisp && typeof window.$crisp.push === 'function') {
      try {
        window.$crisp.push(['do', 'chat:show'])
      } catch (error) {
        console.error('Crisp: 显示聊天窗口失败', error)
      }
    }
  },

  /**
   * 隐藏聊天窗口
   */
  hide: () => {
    if (window.$crisp && typeof window.$crisp.push === 'function') {
      try {
        window.$crisp.push(['do', 'chat:hide'])
      } catch (error) {
        console.error('Crisp: 隐藏聊天窗口失败', error)
      }
    }
  },

  /**
   * 打开聊天窗口
   */
  open: () => {
    if (window.$crisp && typeof window.$crisp.push === 'function') {
      try {
        window.$crisp.push(['do', 'chat:open'])
      } catch (error) {
        console.error('Crisp: 打开聊天窗口失败', error)
      }
    }
  },

  /**
   * 关闭聊天窗口
   */
  close: () => {
    if (window.$crisp && typeof window.$crisp.push === 'function') {
      try {
        window.$crisp.push(['do', 'chat:close'])
      } catch (error) {
        console.error('Crisp: 关闭聊天窗口失败', error)
      }
    }
  },

  /**
   * 发送消息
   * @param {string} message - 要发送的消息
   */
  sendMessage: (message) => {
    if (window.$crisp && message) {
      window.$crisp.push(['do', 'message:send', ['text', message]])
    }
  },

  /**
   * 设置用户信息
   * @param {Object} userData - 用户数据
   */
  setUser: (userData) => {
    if (window.$crisp && userData) {
      if (userData.email) {
        window.$crisp.push(['set', 'user:email', userData.email])
      }
      if (userData.nickname) {
        window.$crisp.push(['set', 'user:nickname', userData.nickname])
      }
      if (userData.phone) {
        window.$crisp.push(['set', 'user:phone', userData.phone])
      }
      if (userData.avatar) {
        window.$crisp.push(['set', 'user:avatar', userData.avatar])
      }
    }
  },

  /**
   * 设置会话数据
   * @param {Object} sessionData - 会话数据
   */
  setSessionData: (sessionData) => {
    if (window.$crisp && sessionData) {
      window.$crisp.push(['set', 'session:data', sessionData])
    }
  },

  /**
   * 检查Crisp是否已加载
   * @returns {boolean}
   */
  isLoaded: () => {
    return !!(window.$crisp && window.CRISP_WEBSITE_ID && typeof window.$crisp.push === 'function')
  },

  /**
   * 检查聊天窗口是否打开
   * @returns {boolean}
   */
  isChatOpened: () => {
    if (window.$crisp) {
      try {
        return window.$crisp.is('chat:opened')
      } catch (error) {
        return false
      }
    }
    return false
  },

  /**
   * 获取未读消息数量
   * @returns {number}
   */
  getUnreadCount: () => {
    if (window.$crisp) {
      try {
        return window.$crisp.get('chat:unread:count') || 0
      } catch (error) {
        return 0
      }
    }
    return 0
  }
}

export default CrispChat
